import type { SerializedError } from "@reduxjs/toolkit"
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query"

import { StatDuration } from "@/types"

export const irohPurple = "#7C7CFF"
export const twZinc500 = "#71717a"
export const n0desGreen = {
  50: "#b9ede3",
  100: "#97d1c6",
  200: "#88bfb5",
  300: "#4aa594",
  400: "#3e8c7d",
  500: "#287c6c",
  600: "#276b5e",
  700: "#236054",
  800: "#20594e",
  900: "#1b4c44",
  950: "#173f37",
}

// greeting that returns good morning/afternoon/evening/night based on a local timestamp
export function greeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return "Good morning"
  if (hour < 18) return "Good afternoon"
  return "Good evening"
}

export function formatDateWithoutMillsecondsAndZ(date: Date) {
  if (!date || Number.isNaN(date.getTime())) {
    console.error(
      "Invalid date passed to formatDateWithoutMillsecondsAndZ:",
      date,
    )
    return new Date().toISOString().split(".")[0]
  }
  return date.toISOString().split(".")[0] // Removes milliseconds and 'Z'
}

export function formatRoundNumber(value: number): string {
  return Math.round(value).toLocaleString()
}

export function validateUsernameOrEmail(
  usernameOrEmail: string,
): string | undefined {
  const un = validateUsername(usernameOrEmail)
  const em = validateEmail(usernameOrEmail)

  if (un && em) {
    return "Enter either a valid username or a valid email"
  }

  return
}

// Usernames can only have:
// - Lowercase Letters (a-z)
// - Numbers (0-9)
// - Dashes (-)
// - Underscores(_)
function isUsernameValid(username: string): boolean {
  return /^[a-z0-9-_]+$/.test(username)
}

const RESERVED_USERNAMES = [
  "me",
  "verify",
  "email",
  "signup",
  "docs",
  "pricing",
  "login",
  "api",
  "admin",
]

// **Keep this in sync with backend/src/model/user.rs#validate_username**
export function validateUsername(
  username: string,
  nameType: "user" | "team" = "user",
): string | undefined {
  const lenValid = 2 <= username.length && username.length <= 128
  const charactersValid = isUsernameValid(username)
  const notReserved = !RESERVED_USERNAMES.includes(username)

  if (charactersValid && lenValid && notReserved) {
    return undefined
  }

  const errors = []
  if (!lenValid) {
    errors.push("must be between 2 and 128 characters")
  }
  if (!charactersValid) {
    errors.push(
      "can only contain lowercase letters, numbers, dashes, and underscores",
    )
  }
  if (!notReserved) {
    errors.push("must not be a reserved username")
  }

  const name = nameType === "team" ? "Team name" : "Username"
  return `${name} ${errors.join(" and ")}.`
}

function isEmailValid(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export function validateEmail(email: string): string | undefined {
  const valid = isEmailValid(email)
  if (valid) {
    return undefined
  }

  return "Please enter a valid email address."
}

// Project Names can only have:
// - Lowercase Letters (a-z)
// - Numbers (0-9)
// - Dashes (-)
function isProjectNameValid(username: string): boolean {
  return /^(?=[a-zA-Z0-9-_]{2,200}$)(?!.*[-_]{2})[^-_].*[^-_]$/.test(username)
}

export function validateProjectName(name: string): string | undefined {
  if (name.length === 0) {
    return "Project name cannot be empty."
  }
  if (!isProjectNameValid(name)) {
    return "Project name must be between 2 and 20 characters and can only contain letters, numbers, dashes, and underscores."
  }

  return undefined
}

function isPasswordValid(password: string): boolean {
  return password.length >= 8
}

export function validatePassword(password: string): string | undefined {
  const valid = isPasswordValid(password)
  if (valid) {
    return undefined
  }

  return "Password must be at least 8 characters long."
}

export function validateDocumentName(name: string): string | undefined {
  if (name.length === 0) {
    return "Document name cannot be empty."
  }
  if (name.length > 200) {
    return "Document name cannot be longer than 200 characters."
  }

  return undefined
}

export function validateDocumentTicket(ticket: string): string | undefined {
  if (ticket.length === 0) {
    return "ticket cannot be empty."
  }
  // ticket must start with "doc"
  if (!ticket.startsWith("doc")) {
    return 'Document ticket must start with "doc".'
  }

  return undefined
}

export function validateBlobTicket(ticket: string): string | undefined {
  if (ticket.length === 0) {
    return "ticket cannot be empty."
  }
  // ticket must start with "blob"
  if (!ticket.startsWith("blob")) {
    return 'Blob ticket must start with "blob".'
  }

  return undefined
}

// max upload size for a single file is 1gb
const MAX_UPLOAD_SIZE = 1024 * 1024 * 1024

export function validateFiles(files: FileList | null): string | undefined {
  if (!files) {
    return "No file selected."
  }
  if (files.length === 0) {
    return "No file selected."
  }

  const totalFileSize = Array.from(files).reduce(
    (acc, file) => acc + file.size,
    0,
  )
  if (totalFileSize > MAX_UPLOAD_SIZE) {
    return "Maximum upload is 1GB total."
  }

  return undefined
}

export function humanDatestamp(date: Date): string {
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
}

export function humanCurrency(num: number): string {
  return `$${num.toLocaleString()}`
}

export function humanNumber(num: number): string {
  if (num === 0) return "0"
  if (!Number.isFinite(num)) return String(num)

  const sizes = ["", "K", "M", "B", "T"]
  const i = Math.floor(Math.log(Math.abs(num)) / Math.log(1000))
  const clampedI = Math.max(0, Math.min(i, sizes.length - 1))

  return `${Number.parseFloat((num / 1000 ** clampedI).toFixed(clampedI === 0 ? 2 : 1))}${
    sizes[clampedI]
  }`
}

// a function that accepts a number of bytes and returns a human readable string
export function humanBytes(bytes: number): string {
  if (bytes === 0) return "0 Bytes"
  if (!Number.isFinite(bytes)) return String(bytes)

  const sizes = ["Bytes", "KB", "MB", "GB", "TB"]
  const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(1024))
  const clampedI = Math.max(0, Math.min(i, sizes.length - 1))

  return `${Number.parseFloat((bytes / 1024 ** clampedI).toFixed(2))} ${sizes[clampedI]}`
}

// relative to now time string (e.g. 5 minutes ago)
// horrible rip-off from GitHub Copilot? Sure.
export function timeAgo(date: Date, now?: Date): string {
  const seconds = Math.floor(
    ((now ?? new Date()).getTime() - date.getTime()) / 1000,
  )

  if (seconds < 60) return "just now"

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60)
    return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`

  const hours = Math.floor(seconds / 3600)
  if (hours < 24) return hours === 1 ? "1 hour ago" : `${hours} hours ago`

  const days = Math.floor(seconds / 86400)
  if (days < 30) return days === 1 ? "1 day ago" : `${days} days ago`

  const months = Math.floor(seconds / 2592000)
  if (months < 12) return months === 1 ? "1 month ago" : `${months} months ago`

  const years = Math.floor(seconds / 31536000)
  return years === 1 ? "1 year ago" : `${years} years ago`
}

// interpret a fetch error into a user-facing string.
export function errorIsStatusCode(
  err: FetchBaseQueryError | SerializedError,
  status: number,
): boolean {
  if ("status" in err) {
    // Error is of type FetchBaseQueryError
    if (typeof err.status === "number") {
      // TODO - HTTP status code interpretation, or do we just run with the error message?
      return err.status === status
    }
  }
  return false
}

export function pctChangeString(current: number, prior: number): string {
  const change = current - prior
  if (current === 0 && prior === 0) return "0.00%"
  if (prior === 0) return "+100.00%"
  const pct = (change / prior) * 100

  return `${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`
}

export function statDurationString(value: StatDuration): string {
  switch (value) {
    case StatDuration.hour:
      return "hour"
    case StatDuration.twentyFourHours:
      return "24 hours"
    case StatDuration.sevenDays:
      return "7 days"
    default:
      return "hour"
  }
}

export function censorEmail(email: string): string {
  const [username, domain] = email.split("@")
  if (!username || !domain) {
    return email // or throw an error if invalid email
  }

  // Censor part of the username. Keep the first letter and the last letter visible.
  // Replace the middle characters with asterisks (*).
  const censoredUsername =
    username.length > 2
      ? username.charAt(0) +
        "*".repeat(username.length - 2) +
        username.charAt(username.length - 1)
      : `${username.charAt(0)}*`

  return `${censoredUsername}@${domain}`
}

// Convert a hex string to a byte array
export function hexToBytes(hex: string) {
  const bytes = []
  for (let c = 0; c < hex.length; c += 2)
    bytes.push(Number.parseInt(hex.substr(c, 2), 16))
  return bytes
}

// Convert a byte array to a hex string
export function bytesToHex(bytes: number[]) {
  const hex = []
  for (let i = 0; i < bytes.length; i++) {
    const current = bytes[i] < 0 ? bytes[i] + 256 : bytes[i]
    hex.push((current >>> 4).toString(16))
    hex.push((current & 0xf).toString(16))
  }
  return hex.join("")
}

// Chart color palette that matches the design system
export const chartColors = [
  "#7C7CFF", // irohPurple - reliable fallback first
  "hsl(12 76 61)", // chart-1 orange-red (light mode)
  "hsl(173 58 39)", // chart-2 teal (light mode)
  "hsl(197 37 24)", // chart-3 dark blue (light mode)
  "hsl(43 74 66)", // chart-4 yellow (light mode)
  "hsl(27 87 67)", // chart-5 orange (light mode)
  "hsl(168 67 48)", // n0des green (primary)
  "hsl(120 67 42)", // Success green
  "hsl(280 65 60)", // Purple
  "hsl(340 75 55)", // Pink
] as const

// Get a color for a specific metric index
export function getChartColor(index: number): string {
  return chartColors[index % chartColors.length]
}
